import { useMemo } from 'react';

import useReferralAnalytics, {
  ReferralAnalyticsQueryParams,
} from 'components/insights/careTransitions/referrals/query/useReferralAnalytics';
import { AccessModes, Dimensions, Metrics, Sources } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import {
  getSlideoutEntityFilter,
  ReferralInsightsSlideoutContext,
  useReferralsInsightsStore,
} from 'stores/referralsInsightsStore';

import {
  getReferralDetailsParams,
  getSlideoutViewByOptions,
  METRIC_DRILL_VIEW_BY_OPTIONS,
  SUB_DIVISION_DIMENSION,
} from '../constants';
import { useReferralsInsightsContext } from '../ReferralsContainer';

export type TopEntity = { name: string; value: number } | null;

export type ViewByQueryResult = {
  rollups: AnalyticsResponseRollupCollection;
  loading: boolean;
};

export type SlideoutAnalytics = {
  // Rollups for the active view-by — fed to the slideout's entity table + header tiles. Always
  // defined (an inactive view-by still returns an empty rollups object); fallback to the sending
  // query's rollups when slideoutDimension isn't set.
  activeRollups: AnalyticsResponseRollupCollection;
  activeLoading: boolean;
  sendingCount: number;
  receivingCount: number;
  fastestResponder: TopEntity;
  slowestResponder: TopEntity;
  topAcceptingReceivingGroup: TopEntity;
  topDenyingReceivingGroup: TopEntity;
  topAcceptingPlan: TopEntity;
  topDenyingPlan: TopEntity;
  countsLoading: boolean;
  // Per-tile loading for the top-by / responder tiles — each is fed by exactly one view-by query
  // so the tile stops spinning the moment its own data lands.
  topReceivingLoading: boolean;
  topPlanLoading: boolean;
  partnerLabel: string;
};

function buildViewByParams(viewByDim: Dimensions): ReferralAnalyticsQueryParams {
  const base = getReferralDetailsParams(viewByDim);
  const subDim = SUB_DIVISION_DIMENSION[viewByDim];
  const baseDims = base.dimensions ?? [];
  const dimsWithSub = subDim ? [viewByDim, subDim, ...baseDims.slice(1)] : baseDims;
  return { ...base, dimensions: dimsWithSub };
}

function viewByDimsForContext(context: ReferralInsightsSlideoutContext | null): Set<Dimensions> {
  if (!context) return new Set();
  if (context.kind === 'metric') return new Set(METRIC_DRILL_VIEW_BY_OPTIONS);
  return new Set(getSlideoutViewByOptions(context.dimension));
}

function useViewByQuery(
  viewByDim: Dimensions,
  enabled: boolean,
  extraFilters: Record<string, unknown> | null
): ViewByQueryResult {
  const params = useMemo(() => buildViewByParams(viewByDim), [viewByDim]);
  const { rollups, loading } = useReferralAnalytics(params, {
    enabled,
    extraFilters,
    queryKey: `referral-slideout-view-by:${viewByDim}`,
  });
  // React Query keeps disabled queries in `pending` state, so `loading` would otherwise be true
  // forever for view-bys not in the current context. Mask it so consumers only see real loading.
  return { rollups, loading: enabled && loading };
}

export default function useSlideoutAnalytics(): SlideoutAnalytics {
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const { predecessorGroupType } = useReferralsInsightsContext();
  const partnerLabel = selectedReceivingGroupType?.displayName || predecessorGroupType?.displayName || 'Partner';

  const extraFilters = useMemo(
    () => (slideoutContext ? getSlideoutEntityFilter(slideoutContext) : null),
    [slideoutContext]
  );

  const activeDims = useMemo(() => viewByDimsForContext(slideoutContext), [slideoutContext]);

  const sendingQuery = useViewByQuery(Dimensions.SENDING_GROUP, activeDims.has(Dimensions.SENDING_GROUP), extraFilters);
  const receivingClientQuery = useViewByQuery(
    Dimensions.RECEIVING_CLIENT,
    activeDims.has(Dimensions.RECEIVING_CLIENT),
    extraFilters
  );
  const receivingGroupQuery = useViewByQuery(
    Dimensions.RECEIVING_GROUP,
    activeDims.has(Dimensions.RECEIVING_GROUP),
    extraFilters
  );
  const planQuery = useViewByQuery(
    Dimensions.HEALTH_PLAN_CLASSIFICATION,
    activeDims.has(Dimensions.HEALTH_PLAN_CLASSIFICATION),
    extraFilters
  );
  const planTypeQuery = useViewByQuery(
    Dimensions.PLAN_TYPE_CLASSIFICATION,
    activeDims.has(Dimensions.PLAN_TYPE_CLASSIFICATION),
    extraFilters
  );

  const slideoutDimension = useReferralsInsightsStore((state) => state.slideoutDimension);

  return useMemo(() => {
    const byDim: Record<string, ViewByQueryResult> = {
      [Dimensions.SENDING_GROUP]: sendingQuery,
      [Dimensions.RECEIVING_CLIENT]: receivingClientQuery,
      [Dimensions.RECEIVING_GROUP]: receivingGroupQuery,
      [Dimensions.HEALTH_PLAN_CLASSIFICATION]: planQuery,
      [Dimensions.PLAN_TYPE_CLASSIFICATION]: planTypeQuery,
    };
    const activeQuery = (slideoutDimension && byDim[slideoutDimension]) || sendingQuery;

    // Subtitle counts read from whichever view-by query carries the dim. Sending dim only lives
    // on the sending query; receiving_group lives on the receiving_client query (as its sub-dim)
    // or on the receiving_group query when that's the active view-by (entity drill).
    const sendingCount = activeDims.has(Dimensions.SENDING_GROUP)
      ? countDistinctFromLeaves(sendingQuery.rollups, Dimensions.SENDING_GROUP)
      : 0;
    const receivingSource = activeDims.has(Dimensions.RECEIVING_CLIENT)
      ? receivingClientQuery.rollups
      : receivingGroupQuery.rollups;
    const receivingCount =
      activeDims.has(Dimensions.RECEIVING_CLIENT) || activeDims.has(Dimensions.RECEIVING_GROUP)
        ? countDistinctFromLeaves(receivingSource, Dimensions.RECEIVING_GROUP)
        : 0;

    // Top-by tiles read from the receiving_client and plan queries' primary-entity rollup level —
    // the rollup row where referral_status is rolled up. At this level every metric is already
    // aggregated correctly by the DB, so we just pick max.
    const perReceivingBranch = primaryEntityRollup(receivingClientQuery.rollups);
    // Top-accepting/denying *plan* tiles must aggregate to the plan level, rolling up plan_type.
    // The primary-entity rollup preserves both plan and plan_type, so it gives a row per plan_type
    // labeled with the plan name — picking max would surface the highest plan_type's rate under
    // the plan's label rather than the plan's true weighted rate.
    const perPlan = rolledUpToPrimary(planQuery.rollups, Dimensions.HEALTH_PLAN_CLASSIFICATION);

    const { fastest, slowest } = pickFastestSlowest(perReceivingBranch);

    return {
      activeRollups: activeQuery.rollups,
      activeLoading: activeQuery.loading,
      sendingCount,
      receivingCount,
      fastestResponder: fastest,
      slowestResponder: slowest,
      topAcceptingReceivingGroup: topByMetric(perReceivingBranch, Dimensions.RECEIVING_GROUP, Metrics.ACCEPTANCE_RATE),
      topDenyingReceivingGroup: topByMetric(perReceivingBranch, Dimensions.RECEIVING_GROUP, Metrics.DENIAL_RATE),
      topAcceptingPlan: topByMetric(perPlan, Dimensions.HEALTH_PLAN_CLASSIFICATION, Metrics.ACCEPTANCE_RATE, {
        noneDisplayName: 'Other',
      }),
      topDenyingPlan: topByMetric(perPlan, Dimensions.HEALTH_PLAN_CLASSIFICATION, Metrics.DENIAL_RATE, {
        noneDisplayName: 'Other',
      }),
      countsLoading: sendingQuery.loading || receivingClientQuery.loading || receivingGroupQuery.loading,
      topReceivingLoading: receivingClientQuery.loading,
      topPlanLoading: planQuery.loading,
      partnerLabel,
    };
  }, [
    slideoutDimension,
    activeDims,
    sendingQuery,
    receivingClientQuery,
    receivingGroupQuery,
    planQuery,
    planTypeQuery,
    partnerLabel,
  ]);
}

// Lightweight side query for a single-dim count breakdown (denial reasons, service refusals,
// response time histogram). Cheaper than folding the dim into the main view-by queries and
// inflating their cardinality.
export function useSlideoutDimensionCounts(
  dimension: Dimensions,
  queryKey: string,
  options: { enabled?: boolean; extraFilters?: Record<string, unknown> | null } = {}
): { names: ReadonlyArray<string>; counts: ReadonlyArray<number | null>; loading: boolean } {
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);
  const { enabled = true, extraFilters = null } = options;
  const params = useMemo<ReferralAnalyticsQueryParams>(
    () => ({
      source: Sources.INSIGHTS_REFERRALS,
      accessModes: [AccessModes.MANAGER],
      dimensions: [dimension],
      metrics: [Metrics.REVIEW_ID_COUNT],
    }),
    [dimension]
  );
  const { data, loading } = useReferralAnalytics(params, {
    enabled: !!slideoutContext && enabled,
    extraFilters,
    queryKey,
  });
  return {
    names: data.dimensions.getCurrentDimensionValuesByIndex(0),
    counts: data.metrics.getCurrentSeries(Metrics.REVIEW_ID_COUNT),
    loading,
  };
}

type Grouping = AnalyticsResponseRollupCollection['groupings'][number];

// Finds the rollup row where the entity (and any sub-entity) dims are preserved but referral_status
// is rolled up.
function primaryEntityRollup(rollups: AnalyticsResponseRollupCollection): Grouping | undefined {
  const statusIdx = rollups.analyticsResponse.findDimensionIndex(Dimensions.REFERRAL_STATUS);
  if (statusIdx < 0) return undefined;
  const totalDims = rollups.analyticsResponse.dimensionHeaders.length;
  const trailingCount = totalDims - statusIdx;
  let bitmask = 0;
  for (let i = 0; i < trailingCount; i++) bitmask |= 1 << i;
  return rollups.groupings.find((g) => g.current[0]?.grouping === bitmask);
}

// Rollup that preserves only the primary entity name, rolling up everything else —
// sub-division dims and status.
function rolledUpToPrimary(rollups: AnalyticsResponseRollupCollection, primaryDim: Dimensions): Grouping | undefined {
  const primaryIdx = rollups.analyticsResponse.findDimensionIndex(primaryDim);
  if (primaryIdx < 0) return undefined;
  const totalDims = rollups.analyticsResponse.dimensionHeaders.length;
  const preserved = new Set([primaryIdx]);
  let bitmask = 0;
  for (let i = 0; i < totalDims; i++) {
    if (!preserved.has(i)) bitmask |= 1 << (totalDims - 1 - i);
  }
  return rollups.groupings.find((g) => g.current[0]?.grouping === bitmask);
}

function dimIndex(source: Grouping, dimension: Dimensions): number {
  return source.analyticsResponse.findDimensionIndex(dimension);
}

// Leaf-row distinct count of a dim. Reads the raw responseData so it works even when the rollups
// collection has stripped grouping=0 rows from its grouping list.
function countDistinctFromLeaves(
  rollups: AnalyticsResponseRollupCollection | undefined,
  dimension: Dimensions
): number {
  if (!rollups) return 0;
  const idx = rollups.analyticsResponse.findDimensionIndex(dimension);
  if (idx < 0) return 0;
  const seen = new Set<string>();
  for (const row of rollups.analyticsResponse.responseData) {
    if (row.grouping !== 0 || row.datesIndex !== 0) continue;
    const value = row.dimensionValues[idx]?.value;
    if (value == null || value === 'None') continue;
    seen.add(String(value));
  }
  return seen.size;
}

function metricRows(
  source: Grouping,
  dimension: Dimensions,
  metric: Metrics,
  options: { noneDisplayName?: string } = {}
): Array<{ name: string; value: number }> {
  const idx = dimIndex(source, dimension);
  if (idx < 0) return [];
  const names = source.dimensions.getCurrentDimensionValuesByIndex(idx);
  const values = source.metrics.getCurrentSeries(metric);
  const { noneDisplayName } = options;
  return names.flatMap((name, i) => {
    const value = values[i];
    if (!name || value == null) return [];
    if (name === 'None') {
      if (!noneDisplayName) return [];
      return [{ name: noneDisplayName, value }];
    }
    return [{ name, value }];
  });
}

function pickFastestSlowest(g: Grouping | undefined): { fastest: TopEntity; slowest: TopEntity } {
  if (!g) return { fastest: null, slowest: null };
  const rows = metricRows(g, Dimensions.RECEIVING_GROUP, Metrics.RESPONSE_TIME_SECONDS_AVERAGE);
  if (rows.length === 0) return { fastest: null, slowest: null };
  let fastest = rows[0];
  let slowest = rows[0];
  for (const r of rows) {
    if (r.value < fastest.value) fastest = r;
    if (r.value > slowest.value) slowest = r;
  }
  return { fastest, slowest };
}

function topByMetric(
  source: Grouping | undefined,
  dimension: Dimensions,
  metric: Metrics,
  options: { noneDisplayName?: string } = {}
): TopEntity {
  if (!source) return null;
  const rows = metricRows(source, dimension, metric, options);
  if (rows.length === 0) return null;
  return rows.reduce((best, r) => (r.value > best.value ? r : best));
}
